// Welcome Bonus — badge + claim + quest popup.

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Gift, Check, Info } from 'lucide-react';
import Modal from '@/components/shared/Modal/Modal';
import { useSignupBonus } from './useSignupBonus';
import { DAILY_BUDGET_USER_MESSAGE, type SignupBonus } from './signupBonusService';
import { typeRole, fsClass } from '@/styles/typography';

const money = (n: number) => `$${(Number(n) || 0).toFixed(2)}`;
const GAMES_ROUTE = '/account/games';

const Confetti: React.FC = () => {
    const colors = ['#F5A623', '#D946EF', '#3FB950', '#58A6FF', '#FF6B6B'];
    const pieces = Array.from({ length: 24 }, (_, i) => i);
    return (
        <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
            <style>{`@keyframes sb-confetti-fall{0%{transform:translateY(-20px) rotate(0);opacity:1}100%{transform:translateY(320px) rotate(540deg);opacity:0}}`}</style>
            {pieces.map((i) => (
                <span
                    key={i}
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: `${(i * 4.3) % 100}%`,
                        width: 8,
                        height: 8,
                        borderRadius: i % 2 ? '50%' : 2,
                        background: colors[i % colors.length],
                        animation: `sb-confetti-fall ${1 + (i % 5) * 0.25}s ${((i % 7) * 0.1).toFixed(2)}s ease-in forwards`,
                    }}
                />
            ))}
        </div>
    );
};

const STATUS_LABEL: Record<SignupBonus['status'], string> = {
    inactive: 'GET BONUS',
    active: 'BONUS ACTIVE',
    in_review: 'IN REVIEW',
    completed: 'BONUS UNLOCKED',
    expired: 'BONUS EXPIRED',
};

const STATUS_COLOR: Record<SignupBonus['status'], string> = {
    inactive: 'text-amber-200 border-amber-300/60 bg-amber-500/25',
    active: 'text-amber-300 border-amber-300/50 bg-amber-500/20',
    in_review: 'text-sky-200 border-sky-300/50 bg-sky-500/20',
    completed: 'text-emerald-300 border-emerald-300/50 bg-emerald-500/20',
    expired: 'text-white/60 border-white/20 bg-white/5',
};

const ProgressQuest: React.FC<{
    title: string;
    tooltip: string;
    current: number;
    target: number;
    met: boolean;
    actionLabel?: string;
    onAction?: () => void;
}> = ({ title, tooltip, current, target, met, actionLabel, onAction }) => {
    const pct = target > 0 ? Math.min(100, (current / target) * 100) : 0;
    return (
        <div className="w-full rounded-2xl border border-white/20 bg-white/[0.03] backdrop-blur-[6px] p-4">
            <div className="flex items-start gap-3 mb-1.5">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 mb-0.5">
                        <span className="text-sm font-bold text-white">{title}</span>
                        <span className="group relative inline-flex">
                            <Info className="w-3.5 h-3.5 text-white/50 cursor-help" />
                            <span className="pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-full mb-1 w-56 rounded-lg bg-black/90 px-3 py-2 text-[length:var(--type-caption)] leading-snug text-white/90 opacity-0 group-hover:opacity-100 transition-opacity z-10 border border-white/10">
                                {tooltip}
                            </span>
                        </span>
                    </div>
                    {met ? (
                        <span className="inline-flex items-center gap-1 text-xs text-emerald-300">
                            <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-emerald-500">
                                <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                            </span>
                            Done
                        </span>
                    ) : (
                        <span className="text-xs font-mono text-white/70">
                            {money(current)} / {money(target)}
                        </span>
                    )}
                </div>
                {actionLabel && onAction && !met && (
                    <button
                        type="button"
                        onClick={onAction}
                        className="shrink-0 rounded-lg border border-amber-300/40 bg-amber-500/20 px-3 py-1.5 text-[length:var(--type-caption)] font-black uppercase tracking-wide text-amber-100 transition-transform hover:scale-105 active:scale-95"
                    >
                        {actionLabel}
                    </button>
                )}
            </div>
            <div className="h-2.5 w-full rounded-full bg-white/10 overflow-hidden">
                <div
                    className={`h-full rounded-full transition-all duration-500 ${met ? 'bg-emerald-500' : 'bg-gradient-to-r from-amber-400 to-orange-500'}`}
                    style={{ width: `${met ? 100 : pct}%` }}
                />
            </div>
        </div>
    );
};

const QuestModal: React.FC<{
    bonus: SignupBonus;
    onClose: () => void;
    claiming: boolean;
    claimError: string | null;
    onClaim: () => Promise<void>;
}> = ({ bonus, onClose, claiming, claimError, onClaim }) => {
    const navigate = useNavigate();
    const goPlay = () => { onClose(); navigate(GAMES_ROUTE); };
    const grant = Number(bonus.grantedAmount) || 0;
    const liveNet = Number(bonus.netWin) || 0;
    const floorLiftedByLoss = bonus.floorLiftedByLoss === true || liveNet <= -grant;
    const claimBlocked = bonus.claimBlockCode === 'DAILY_BUDGET';
    const notice =
        claimError
        || bonus.claimBlockMessage
        || (claimBlocked ? DAILY_BUDGET_USER_MESSAGE : null);

    return createPortal(
        <Modal isOpen onClose={onClose} size="sm" title="Welcome Bonus">
            <div className="flex flex-col items-center text-center mb-5">
                <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 mb-3">
                    <Gift className="w-7 h-7 text-white" />
                </div>
                <h2 className="text-xl font-black text-white italic">
                    {money(bonus.grantedAmount)} Welcome Bonus
                </h2>
                <p className="text-sm text-caption-muted mt-1">
                    {bonus.status === 'inactive' && 'Claim your welcome bonus credits to start playing.'}
                    {bonus.status === 'active' && `Playing with ${money(bonus.grantedAmount)} bonus credits.`}
                    {bonus.status === 'in_review' && 'Quest complete — first payout needs manual approval.'}
                    {bonus.status === 'completed' && 'Welcome bonus settled — you can withdraw your full balance.'}
                    {bonus.status === 'expired' && 'This bonus expired after inactivity — grant credits were recalled.'}
                </p>
            </div>

            {bonus.status === 'inactive' && (
                <div className="flex flex-col gap-3">
                    {notice && (
                        <div
                            className="rounded-xl border border-amber-400/30 bg-amber-500/10 px-4 py-3 text-center"
                            data-testid="signup-bonus-claim-notice"
                        >
                            <p className="text-sm text-amber-100">{notice}</p>
                        </div>
                    )}
                    <button
                        type="button"
                        disabled={claiming || claimBlocked}
                        onClick={() => { void onClaim(); }}
                        data-testid="signup-bonus-claim-cta"
                        className="w-full rounded-xl bg-gradient-to-r from-amber-400 to-orange-500 px-4 py-3 text-sm font-black uppercase tracking-wide text-white transition-transform hover:scale-[1.02] active:scale-95 disabled:opacity-60"
                    >
                        {claiming ? 'Activating…' : 'Get Welcome Bonus'}
                    </button>
                    <p className="text-[length:var(--type-caption)] leading-snug text-white/50 text-center px-2">
                        Credits come from your shop (or the platform). If they do not have enough credits, try again later.
                    </p>
                </div>
            )}

            {bonus.status === 'active' && (
                <div className="flex flex-col gap-4">
                    {/* Event P&L — scoped to Welcome Bonus (since claim), not all-time */}
                    <div className="rounded-2xl border border-white/20 bg-white/[0.04] px-4 py-4 text-center">
                        <p className="text-[length:var(--type-caption)] font-semibold uppercase tracking-wider text-white/50 mb-1">
                            Bonus event P&L
                        </p>
                        <p
                            className={`text-3xl font-black tabular-nums ${
                                liveNet > 0 ? 'text-emerald-300' : liveNet < 0 ? 'text-rose-300' : 'text-white'
                            }`}
                            data-testid="signup-bonus-live-pnl"
                        >
                            {liveNet > 0 ? '+' : ''}{money(liveNet)}
                        </p>
                        <p className="mt-2 text-[length:var(--type-caption)] text-white/45">
                            Cash out − sent to game, only since you claimed this bonus
                        </p>
                        <p className="mt-1 text-[length:var(--fs-base)] text-white/35">
                            Not your all-time game P&L
                        </p>
                    </div>

                    {/* Event peak → In review */}
                    <ProgressQuest
                        title="Event peak unlock"
                        tooltip={`Hit ${money(bonus.winThreshold)} peak event P&L (highest since claim) to enter In review. Peak never goes down. This is not all-time P&L.`}
                        current={Math.max(0, Number(bonus.peakNetWin) || 0)}
                        target={bonus.winThreshold}
                        met={bonus.netWinMet}
                        actionLabel="Play"
                        onAction={goPlay}
                    />

                    {bonus.netWinMet ? (
                        <div className="rounded-xl border border-sky-400/30 bg-sky-500/10 px-4 py-3 text-center">
                            <p className="text-sm font-semibold text-sky-200">Peak reached</p>
                            <p className="mt-1 text-xs text-white/70">
                                Refreshing status… first payout will need manual approval (In review).
                            </p>
                        </div>
                    ) : floorLiftedByLoss ? (
                        <div className="rounded-xl border border-emerald-400/30 bg-emerald-500/10 px-4 py-3 text-center">
                            <p className="text-sm font-semibold text-emerald-200">Floor lifted</p>
                            <p className="mt-1 text-xs text-white/70">
                                Event P&L ≤ −{money(grant).slice(1)} — withdraw freely until event P&L recovers above that.
                            </p>
                        </div>
                    ) : (
                        <div className="rounded-xl border border-amber-300/30 bg-amber-500/10 px-4 py-3 text-center">
                            <p className="text-sm font-semibold text-amber-200">
                                Keep ≥ {money(grant)} in wallet
                            </p>
                            <p className="mt-1 text-xs text-white/70">
                                Unlock at {money(bonus.winThreshold)} event peak to enter In review, or floor lifts at −{money(grant).slice(1)} event P&L.
                            </p>
                        </div>
                    )}
                </div>
            )}

            {bonus.status === 'in_review' && (
                <div className="flex flex-col gap-3">
                    <div className="rounded-2xl border border-sky-400/30 bg-sky-500/[0.08] px-4 py-4 text-center">
                        <p className="text-sm font-black uppercase tracking-wide text-sky-200">
                            Event P&L quest complete
                        </p>
                        <p className="mt-2 text-xs text-white/70">
                            No wallet floor. Every payout request must be approved manually
                            (shop for shop players, admin for platform players).
                            After the first approval, future payouts follow normal rules.
                        </p>
                    </div>
                    <ProgressQuest
                        title="Event peak unlock"
                        tooltip={`Hit ${money(bonus.winThreshold)} peak event P&L (highest since claim). Peak never goes down.`}
                        current={Math.max(0, Number(bonus.peakNetWin) || 0)}
                        target={bonus.winThreshold}
                        met
                    />
                </div>
            )}

            {bonus.status === 'completed' && (
                <div className="relative rounded-2xl border border-emerald-400/30 bg-emerald-500/[0.08] backdrop-blur-[6px] px-4 py-5 text-center overflow-hidden">
                    <Confetti />
                    <div className="relative">
                        <p className="text-sm font-black uppercase tracking-wide text-emerald-300">
                            Welcome bonus unlocked
                        </p>
                        <p className="text-[length:var(--type-caption)] text-white/60 mt-1">You can withdraw your full balance any time.</p>
                    </div>
                </div>
            )}

            {bonus.status === 'expired' && (
                <div className="rounded-[8px] border border-border-ice bg-[#0a1634] px-4 py-3 text-center">
                    <p className="text-sm text-white/60">
                        This bonus expired after inactivity. Grant credits were recalled — withdrawals are no longer floored.
                    </p>
                </div>
            )}
        </Modal>,
        document.body
    );
};

const SignupBonusBadge: React.FC<{ compact?: boolean }> = ({ compact = false }) => {
    const { bonus, claiming, claimError, setClaimError, claim } = useSignupBonus();
    const [open, setOpen] = useState(false);

    useEffect(() => {
        if (bonus?.status !== 'in_review' && bonus?.status !== 'completed') return;
        const key = bonus.status === 'in_review' ? 'sb-in-review-opened' : 'sb-completed-celebrated';
        if (localStorage.getItem(key) === '1') return;
        localStorage.setItem(key, '1');
        setOpen(true);
    }, [bonus?.status]);

    if (!bonus) return null;

    const handleClaim = async () => {
        try {
            await claim();
        } catch {
            // claimError already set on hook
        }
    };

    return (
        <>
            <button
                type="button"
                onClick={() => {
                    setClaimError(null);
                    setOpen(true);
                }}
                className={`inline-flex items-center gap-1 border font-black uppercase backdrop-blur-md transition-transform hover:scale-105 active:scale-95 ${STATUS_COLOR[bonus.status]} ${compact ? `h-8 px-2 gap-1 rounded-[10px] ${fsClass.base}` : `h-10 px-3 gap-1.5 rounded-[12px] ${fsClass.base}`}`}
                aria-label="View welcome bonus"
                data-testid="signup-bonus-badge"
            >
                <Gift className={compact ? 'w-3.5 h-3.5' : 'w-4 h-4'} />
                <span className="hidden sm:inline">{STATUS_LABEL[bonus.status]}</span>
            </button>
            {open && (
                <QuestModal
                    bonus={bonus}
                    onClose={() => setOpen(false)}
                    claiming={claiming}
                    claimError={claimError}
                    onClaim={handleClaim}
                />
            )}
        </>
    );
};

export default SignupBonusBadge;
